/**
 * AI pod flow — inline-SVG graphic explaining how the system works.
 *
 * Four stations connected by animated dotted lines: market signals →
 * AI pod (whale cluster) → exchange orders → profit. Each station gets
 * a title + one-line body from i18n. CSS keyframes drive the dashed
 * line "flow" animation; respects prefers-reduced-motion via the
 * global rule.
 */

import { useTranslation } from 'react-i18next';

import { Card } from './ui';
import { WhaleGlyph } from './BrandMark';

export function AiFlow() {
  const { t } = useTranslation();

  return (
    <Card variant="paper" pad="lg" className="pb-grain overflow-hidden">
      <h3 className="pb-display text-[20px] leading-tight">
        {t('ai.title')}
      </h3>

      <div className="mt-4 flex flex-col gap-3">
        <FlowRow
          index={1}
          icon={<SignalIcon />}
          title={t('ai.signals')}
          body={t('ai.signals_desc')}
        />
        <FlowRow
          index={2}
          icon={<WhaleGlyph size={22} />}
          title={t('ai.ai_filter')}
          body={t('ai.ai_filter_desc')}
          accent
        />
        <FlowRow
          index={3}
          icon={<ExchangeIcon />}
          title={t('ai.order')}
          body={t('ai.order_desc')}
        />
        <FlowRow
          index={4}
          icon={<ProfitIcon />}
          title={t('ai.profit')}
          body={t('ai.profit_desc')}
        />
      </div>
    </Card>
  );
}

function FlowRow({
  index,
  icon,
  title,
  body,
  accent,
}: {
  index: number;
  icon: React.ReactNode;
  title: string;
  body: string;
  accent?: boolean;
}) {
  const isLast = index === 4;
  return (
    <div className="flex items-stretch gap-3">
      <div className="flex flex-col items-center">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            accent
              ? 'bg-pb-accent text-[#1a1105] ring-2 ring-pb-accent-soft'
              : 'bg-pb-paper2 text-pb-accent'
          }`}
        >
          {icon}
        </div>
        {isLast ? null : <FlowConnector />}
      </div>
      <div className={`flex-1 ${isLast ? '' : 'pb-2'}`}>
        <div className="text-[14px] font-semibold text-tg-text">{title}</div>
        <p className="mt-0.5 text-[13px] leading-relaxed text-tg-hint">{body}</p>
      </div>
    </div>
  );
}

function FlowConnector() {
  return (
    <span
      aria-hidden
      className="block w-[2px] flex-1 min-h-[18px]"
      style={{
        background:
          'repeating-linear-gradient(to bottom, var(--pb-accent-line) 0 4px, transparent 4px 8px)',
      }}
    />
  );
}

function SignalIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden fill="none">
      <path
        d="M3 13h3l2.5-6 3 12 2.5-6h3l2 3h2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ExchangeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden fill="none">
      <path
        d="M4 8h12l-3-3M20 16H8l3 3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProfitIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden fill="none">
      <path
        d="M12 20V4M6 10l6-6 6 6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
